import { BroadcastChannel } from 'broadcast-channel';
import { v4 } from 'uuid';
import { uniDirectionSerializer } from '../serializers/json.mjs';

const constants = /* @__PURE__ */ (() => {
  const NAMESPACE_PREFIX = "flexTeam.authn";
  const isTest = process.env.NODE_ENV === "test";
  const isBrowser = typeof window !== "undefined" || isTest;
  const isServer = typeof window === "undefined";
  const BROADCAST_CHANNEL_OPTIONS = {
    webWorkerSupport: false,
    ...isTest ? { type: "simulate" } : {}
  };
  return { NAMESPACE_PREFIX, isBrowser, isServer, BROADCAST_CHANNEL_OPTIONS };
})();
class Bus {
  constructor(params) {
    this.params = params;
    ///
    this.id = v4();
    this.innerNamespace = `${constants.NAMESPACE_PREFIX}.${this.params.namespace}`;
    this.isBusEvent = (e) => e instanceof CustomEvent && typeof e.detail?.instigator === "string";
    this.disposers = /* @__PURE__ */ new Set();
    this.channel = constants.isServer ? void 0 : new BroadcastChannel(
      this.innerNamespace,
      constants.BROADCAST_CHANNEL_OPTIONS
    );
    this.dead = false;
    this.lastPayload = uniDirectionSerializer(this.params.getPayload());
    this.initialize = () => {
      const { isBusEvent, innerNamespace, disposers, channel } = this;
      const { skipInitialProbing } = this.params;
      const handleCustomEvent = (e) => {
        if (!isBusEvent(e)) return;
        const { detail } = e;
        this.handleMessage(detail);
      };
      window.addEventListener(innerNamespace, handleCustomEvent);
      channel?.addEventListener("message", this.handleMessage);
      disposers.add(() => {
        window.removeEventListener(innerNamespace, handleCustomEvent);
        channel?.close();
      });
      if (!skipInitialProbing) this.emitProbe();
    };
    this.handleMessage = (message) => {
      const { id: instigator } = this;
      if (message.instigator === instigator) return;
      const { getPayload, onMessage } = this.params;
      switch (message.type) {
        case "probe": {
          const payload = getPayload();
          this.emitPayload(payload);
          return;
        }
        case "broadcast": {
          const { payload } = message;
          const serialized = uniDirectionSerializer(payload);
          if (this.lastPayload === serialized) return;
          this.lastPayload = serialized;
          onMessage(payload);
          return;
        }
      }
    };
    // emit probation (= ask others for their payload)
    this.emitProbe = () => {
      const { dead, innerNamespace, channel, id: instigator } = this;
      if (dead) return;
      const detail = { instigator, type: "probe" };
      window.dispatchEvent(new CustomEvent(innerNamespace, { detail }));
      channel?.postMessage(detail);
    };
    this.emitPayload = (payload) => {
      const { dead, params, innerNamespace, channel, id: instigator } = this;
      if (dead) return;
      const detail = { instigator, type: "broadcast", payload };
      const { skipCustomEventEmit } = params;
      if (!skipCustomEventEmit) {
        window.dispatchEvent(
          new CustomEvent(innerNamespace, { detail })
        );
      }
      channel?.postMessage(detail);
    };
    /** Emit event globally (current tab + other tabs) */
    this.emit = (payload) => this.emitPayload(payload);
    /** Destructor - cleans up event handlers / channels */
    this.destroy = () => {
      this.dead = true;
      this.disposers.forEach((dispose) => dispose());
    };
    if (!constants.isBrowser) {
      this.destroy();
      return;
    }
    this.initialize();
  }
}

export { Bus };
//# sourceMappingURL=class.mjs.map
